import { useLocation, Link } from 'react-router';
import { mutateConfig } from '../api/mutationConfig';
import { DetailHeader, Section, KeyValueGrid } from '../components/DetailSection';
import { TransactionStatusBadge } from '../components/TransactionStatusBadge';
import { formatKobo } from '../lib/money';
import { formatDateTime } from '../lib/date';
import { TransactionCategory, TransactionStatus, formatTransactionCategory } from '../constants/transaction';
import type { Transaction } from '../components/TransactionsTable';

const RETRYABLE_STATUSES: string[] = [TransactionStatus.Failed, TransactionStatus.Reversed];

const isRetryablePayout = (t: Transaction) =>
  t.category?.toLowerCase() === TransactionCategory.Payout && RETRYABLE_STATUSES.includes(t.status?.toLowerCase());

const RetryFailedPayoutButton = ({ reference }: { reference: string }) => {
  const { mutate: retryFailedPayout, isPending, isSuccess, isError } = mutateConfig()<void>({
    url: `/v1/ops/payouts/${encodeURIComponent(reference)}/retry`,
    cacheKeys: ['transactions'],
  });

  const handleClick = () => {
    if (window.confirm('Retry this payout? The transfer will be attempted again.')) retryFailedPayout(undefined);
  };

  return (
    <div className="flex items-center justify-end gap-3">
      {isSuccess && <span className="text-sm text-gray-500">Retry queued.</span>}
      {isError && <span className="text-sm text-red-500">Failed to retry payout.</span>}
      <button
        type="button"
        onClick={handleClick}
        disabled={isPending || isSuccess}
        className="py-2 px-4 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {isPending ? 'Retrying...' : 'Retry'}
      </button>
    </div>
  );
};

export const TransactionDetail = () => {
  const { state } = useLocation();
  const { transaction = null, search = '' } = (state as { transaction?: Transaction; search?: string } | null) ?? {};
  const backTo = search ? `/transactions?${search}` : '/transactions';

  if (!transaction) {
    return (
      <div className="flex flex-col gap-3">
        <Link to="/transactions" className="text-xs text-gray-500 hover:text-gray-900 transition-colors">
          ← Transactions
        </Link>
        <p className="text-sm text-gray-500">Open a transaction from the list to view its details.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <DetailHeader
        backTo={backTo}
        backLabel="Transactions"
        title={<span className="font-mono text-lg">{transaction.transaction_reference}</span>}
        badges={<TransactionStatusBadge status={transaction.status} />}
      />
      <Section title="Summary">
        <KeyValueGrid
          items={[
            { label: 'Amount', value: <span className="text-base font-semibold">{formatKobo(transaction.amount, { sign: true })}</span> },
            { label: 'Type', value: <span className="capitalize">{transaction.type}</span> },
            { label: 'Category', value: formatTransactionCategory(transaction.category) },
            { label: 'Status', value: <TransactionStatusBadge status={transaction.status} /> },
            { label: 'Transaction ID', value: <span className="font-mono text-xs">{transaction.uid}</span> },
            { label: 'Reference', value: <span className="font-mono text-xs">{transaction.reference}</span> },
            { label: 'Narration', value: transaction.narration || '—' },
            { label: 'Date & Time', value: formatDateTime(transaction.updated_at || transaction.created_at) },
            { label: 'Wallet balance after', value: formatKobo(transaction.balance) },
          ]}
        />
      </Section>
      <Section title="Linked records">
        <KeyValueGrid
          items={[
            { label: 'Name', value: transaction.name },
            { label: 'Program', value: transaction.program_name },
          ]}
        />
      </Section>
      {isRetryablePayout(transaction) && <RetryFailedPayoutButton reference={transaction.reference} />}
    </div>
  );
};
